import { h } from "@stencil/core";
/**
 * Wraps a native checkbox and provides styling to them. In addition it provides some behavior which is described below:
 * <ul>
 * <li>If the attribute 'fid' receives an value the value is given to the checkbox for the attribute 'id' and to the provided label for the attribute 'for'. By this it is ensured that both values could be managed at one central place and could not diverge.
 * <li>If the state does not have the value State.neutral a proper state icon will be displayed automatically.
 * </ul>
 */
export class BbgCheckbox {
  constructor() {
    this.fid = "";
    this.indeterminate = false;
  }
  render() {
    const cssClasses = {
      "a-checkbox": true,
      "a-checkbox--indeterminate": this.indeterminate,
    };
    const input = this.hostElement.querySelector("input");
    input.addEventListener("click", () => {
      this.indeterminate = false;
    });
    return (h("div", { class: cssClasses }, h("slot", null)));
  }
  componentWillLoad() {
    this.observer = new MutationObserver(() => this.updateIdAndForReference());
    this.observer.observe(this.hostElement, { childList: false, subtree: false, attributes: true, attributeFilter: ["fid"] });
    this.updateIdAndForReference();
  }
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  updateIdAndForReference() {
    let inputElement = this.provideInputElement();
    let labelElement = this.provideLabelElement();
    if (inputElement && this.fid !== "") {
      inputElement.setAttribute("id", this.fid);
    }
    if (labelElement && this.fid !== "") {
      labelElement.setAttribute("for", this.fid);
    }
  }
  provideInputElement() {
    return this.hostElement.querySelector("input");
  }
  provideLabelElement() {
    return this.hostElement.querySelector("label");
  }
  static get is() { return "bbg-checkbox"; }
  static get originalStyleUrls() {
    return {
      "$": ["bbg-checkbox.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["bbg-checkbox.css"]
    };
  }
  static get properties() {
    return {
      "fid": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "The field ID.\nThe field ID will be used to manage the label and input field reference. This means that the label receives an attribute 'for' with the given name and the checkbox receives an attribute 'id' with\nthe given name. By this the label to input reference could be managed at one single place"
        },
        "attribute": "fid",
        "reflect": false,
        "defaultValue": "\"\""
      },
      "indeterminate": {
        "type": "boolean",
        "mutable": true,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Activates the indeterminate selection where the checkbox is neither checked nor unchecked (an _in-between-state_).\nSemantically it means that the user did not actively made a decision (yet).\nThe indeterminate selection gets removed automatically after an interaction with the checkbox."
        },
        "attribute": "indeterminate",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get elementRef() { return "hostElement"; }
}
